import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { fmt } from "../desk/deskData";
import type { StructureRead } from "./marketStructure";
import type { BiasCheck, Vote } from "./structure";

const TONE = { up: "text-profit", down: "text-loss" } as const;
const WORD = { bullish: "Bullish", bearish: "Bearish", neutral: "Neutral" } as const;

/** A vote's points: "+0.4", "−0.3", "0". */
function points(vote: Vote, weight: number): string {
  return vote === "up" ? `+${weight}` : vote === "down" ? `−${weight}` : "0";
}

function Part({ vote, weight, children }: { vote: Vote; weight: number; children: ReactNode }) {
  return (
    <span>
      {children} <span className={cn("num font-semibold", vote ? TONE[vote] : "text-muted")}>{points(vote, weight)}</span>
    </span>
  );
}

/**
 * The desk's 4h bias in one legend line, each vote with its points, so the
 * verdict adds up on screen: "4h bias Bullish +0.7 · EMA 20 > 50 > 200 +0.4 ·
 * RSI 61.2 > 55 +0.3 · swings mixed 0". Over +0.2 is bullish, under −0.2
 * bearish (backend htfBiasFilter determineHTFBias).
 */
export function BiasLegendRow({ check }: { check: BiasCheck }) {
  const { ema, rsi, swings } = check;
  const net = (ema.vote === "up" ? 0.4 : ema.vote === "down" ? -0.4 : 0)
    + (rsi.vote === "up" ? 0.3 : rsi.vote === "down" ? -0.3 : 0)
    + (swings.vote === "up" ? 0.3 : swings.vote === "down" ? -0.3 : 0);
  const r = rsi.value.toFixed(1);
  return (
    <div className="flex flex-wrap items-center gap-x-2.5">
      <span>
        4h bias{" "}
        <span className={cn("font-semibold", check.direction === "bullish" ? TONE.up : check.direction === "bearish" ? TONE.down : "text-foreground")}>
          {WORD[check.direction]}
        </span>{" "}
        <span className="num text-foreground">{net > 0 ? "+" : net < 0 ? "−" : ""}{Math.abs(net).toFixed(1)}</span>
      </span>
      <Part vote={ema.vote} weight={0.4}>
        {ema.vote === "up" ? "EMA 20 > 50 > 200" : ema.vote === "down" ? "EMA 20 < 50 < 200" : ema.e200 === null ? "EMA 200 needs 200 candles" : "EMAs not in order"}
      </Part>
      <Part vote={rsi.vote} weight={0.3}>
        {rsi.vote === "up" ? `RSI ${r} > 55` : rsi.vote === "down" ? `RSI ${r} < 45` : `RSI ${r} (45–55)`}
      </Part>
      <Part vote={swings.vote} weight={0.3}>
        {swings.vote === "up" ? "highs rising" : swings.vote === "down" ? "lows falling" : "swings mixed"}
      </Part>
    </div>
  );
}

/**
 * The structure's trend in one legend line: "Structure 15m Uptrend broken ·
 * closed below the last higher low 5.266".
 */
export function StructureLegendRow({ read, timeframe }: { read: StructureRead; timeframe: string }) {
  const tf = timeframe === "1d" ? "1D" : timeframe;
  const p = read.pivot;
  const up = read.trend === "up";
  const [word, tone, detail] =
    read.trend === "range" || !p
      ? ["No clear trend", "text-foreground", "the last highs and lows disagree"]
      : read.broken
        ? [`${up ? "Uptrend" : "Downtrend"} broken`, "text-warning", `closed ${up ? "below the last higher low" : "above the last lower high"} ${fmt(p.price)}`]
        : [
            up ? "Uptrend" : "Downtrend",
            up ? TONE.up : TONE.down,
            `${up ? "higher highs and higher lows · holds while above" : "lower highs and lower lows · holds while below"} ${fmt(p.price)}`,
          ];
  return (
    <div className="flex flex-wrap items-center gap-x-1.5">
      <span>Structure {tf}</span>
      <span className={cn("font-semibold", tone)}>{word}</span>
      <span>· {detail}</span>
      <Link
        to="/playbook#structure"
        className="pointer-events-auto text-foreground/85 underline decoration-dotted underline-offset-2 outline-none focus-visible:ring-2 focus-visible:ring-focus"
      >
        learn
      </Link>
    </div>
  );
}
